/**
 * @typedef {ReturnType<typeof import('widgets/forms/InputRadio').default>} InputRadio
 */

/**
 * @param {InputRadio} InputRadio Base widget for extending
 * @returns {typeof RadioSelector} Radio Selector widget
 */
export default function (InputRadio) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class RadioSelector
     * @augments InputRadio
     * @classdesc Radio selector component with next features:
     * 1. Rerenders selector by calling {@link RadioSelector#updateState} method from parrent widget
     * 2. Creates focus on selected item by calling {@link RadioSelector#focusInput} method from parrent widget
     * @example <caption>Example of radio selector for billing addresses</caption>
     * <div
     *     data-widget="radioSelector"
     *     data-id="billingAddressSelector"
     *  >
     *     <isloop items=${addresses} var="address">
     *         <div>
     *             <input
     *                 id="billing-${address.addressUUID}"
     *                 value="${address.addressUUID}"
     *                 type="radio"
     *                 name="savedAddress"
     *                 data-id="savedAddress-${address.addressUUID}"
     *                 data-ref="field"
     *                 data-value="${address.addressUUID}"
     *                 data-event-change="update"
     *                 <isif condition="${address.selected}">checked</isif>
     *                 data-first-name="${address.firstName}"
     *                 ...
     *             />
     *                 ...
     *                 <button
     *                     type="button"
     *                     data-event-click.prevent="updateAddress"
     *                     aria-describedby="billing-address-details-${address.addressUUID}"
     *                     data-uuid="${address.addressUUID}"
     *                 >
     *                     ${Resource.msg('button.edit', 'checkout', null)}
     *                 </button>
     *         </div>
     *     </isloop>
     * </div>
     */
    class RadioSelector extends InputRadio {
        /**
         * @description Rerender radio selector with new data
         * @param {object} data - Data necessary for template rendering
         * @returns {Promise} Template rendering promise
         */
        updateState(data) {
            return this.render(undefined, data);
        }

        /**
         * @description Creates focus on the selected element
         * @returns {void}
         */
        focusInput() {
            const input = this.getChecked();
            if (input) {
                input.focus();
            }
        }
    }

    return RadioSelector;
}
